import {
  escapeNewLine,
  formatDate,
  formatNZD,
  sanitizeText,
} from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import React from "react";
import { InvoiceExportSummary } from "types/model.types";

interface IAdminInvoiceViewProps {
  invoice: InvoiceExportSummary;
}
const AdminInvoiceView: React.FC<IAdminInvoiceViewProps> = ({ invoice }) => {
  return (
    <>
      <PreviewHeader />
      <div className="sub-title">*** Invoice ***</div>
      <div className="spacer padTop padBottom" />
      <table className="print-table">
        <tbody>
          <tr className="vat">
            <td className="leftCol">
              <span className="rcl">Date Ordered:</span>
              <span className="rcv">
                {formatDate(
                  invoice?.summary.createdOn ?? new Date().toDateString(),
                )}
              </span>
            </td>
            <td className="rightCol">
              <div>
                <span className="rcl">TAX INVOICE NO:</span>
                <span className="rcv">{invoice.id}</span>
              </div>
              <div>
                <span className="rcl">CUSTOMER NO:</span>
                <span className="rcv">{invoice?.invoiceTo.id}</span>
              </div>
              <div>
                <span className="rcl">GST:</span>
                <span className="rcv">
                  {process.env.REACT_APP_GST_NO ?? "20-898-763"}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="spacer padTop padBottom" />
      <table className="print-table">
        <tbody>
          <tr className="vat">
            <td style={{ width: "50%" }} className="leftCol">
              <span className="heading">Invoice To</span>
              <div className="divider" />
            </td>
            <td style={{ width: "50%" }}>
              <span className="heading">Deliver To</span>
              <div className="divider" />
            </td>
          </tr>
          <tr className="vat">
            <td>
              <div
                dangerouslySetInnerHTML={{
                  __html: escapeNewLine(
                    [
                      invoice?.invoiceTo.debtorName,
                      invoice?.invoiceTo.addressLine1,
                      invoice?.invoiceTo.addressLine2,
                      invoice?.invoiceTo.addressLine3,
                    ]
                      .filter(Boolean)
                      .join("\n"),
                  ),
                }}
              />
            </td>
            <td>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    String(invoice?.invoiceTo).trim() ===
                    String(invoice?.deliverTo).trim()
                      ? (invoice?.deliverTo.debtorName ?? "")
                      : [
                          invoice?.deliverTo.debtorName,
                          invoice?.deliverTo.addressLine1,
                          invoice?.deliverTo.addressLine2,
                          invoice?.deliverTo.addressLine3,
                        ]
                          .filter(Boolean)
                          .join("\n"),
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="spacer padTop padBottom" />
      <table className="print-table">
        <tbody>
          <tr>
            <td>
              <span className="bold">Your Order No: </span>
              {invoice?.summary.yourOrderNo ?? invoice?.summary.customerOrderNo}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="spacer padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Product</th>
            <th className="description">Description</th>
            <th className="money">Ordered</th>
            <th className="money">Supplied</th>
            <th className="money">E/Price</th>
            <th className="money">C/Price</th>
            <th className="money">Discount %</th>
            <th className="money">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(invoice?.summary.notes ?? ""),
                }}
              />
            </td>
            <td colSpan={6}> </td>
          </tr>
          {invoice?.orderDetails.map((item, index) => (
            <tr key={`odt-${item.id}-${index}`}>
              <td className="nowrap">{`${item.id}${item.productCode.toUpperCase()}`}</td>
              <td className="description">{item.description}</td>
              <td className="money">{item.ordered ?? 0}</td>
              <td className="money">{item.supplied ?? 0}</td>
              <td className="money">{formatNZD(item.eachPrice)}</td>
              <td className="money">{formatNZD(item.cartonPrice)}</td>
              <td className="money">
                {item.discount > 0 ? item.discount : ""}
              </td>
              <td className="money">{formatNZD(item.total)}</td>
            </tr>
          ))}
          <tr>
            <td></td>
            <td>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(invoice?.summary.notes2 ?? ""),
                }}
              />
            </td>
            <td colSpan={6}>{""}</td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td className="money">Sub Total</td>
            <td className="money">{formatNZD(invoice?.summary.total ?? 0)}</td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td className="money">GST</td>
            <td className="money">{formatNZD(invoice?.summary.gst ?? 0)}</td>
          </tr>
          <tr>
            <td colSpan={6} />
            <td className="money money-focal">TOTAL</td>
            <td className="money money-focal">
              {formatNZD(
                (invoice?.summary.total ?? 0) + (invoice?.summary.gst ?? 0),
              )}
            </td>
          </tr>
          <tr>
            <td className="disclaimer" colSpan={8}>
              <br />
              NOTE: THE GOODS SHOWN ON THIS INVOICE WILL CONTINUE TO REMAIN THE
              PROPERTY OF JOHN JACOBSON LTD UNTIL THIS INVOICE HAS BEEN PAID IN
              FULL
            </td>
          </tr>
          <tr>
            <td colSpan={8}>
              Direct Credit payments can be made to: <br />
              Westpac, Petone, Account <em>03-0543-0207702-00</em>
            </td>
          </tr>
          <tr>
            <td colSpan={8}>
              Printed on: {formatDate(new Date().toDateString())}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default AdminInvoiceView;
